import { useEffect, useId, useRef, useState } from 'react';
import { useAuth } from '../lib/auth';
import { useSavedArtists } from '../lib/savedArtists';
import { Modal } from './Modal';
import { useToast } from './Toast';

function Avatar({ name, src, size }: { name: string; src: string | null; size: number }) {
  const [failed, setFailed] = useState(false);
  if (src && !failed) {
    // Google avatars refuse hotlinks that send a Referer.
    return (
      <img
        src={src}
        alt=""
        width={size}
        height={size}
        referrerPolicy="no-referrer"
        onError={() => setFailed(true)}
        style={{ width: size, height: size, borderRadius: 999, objectFit: 'cover' }}
      />
    );
  }
  return <>{(name.trim()[0] ?? 'S').toUpperCase()}</>;
}

/** The "S" avatar (FR-U, account menu): Sign in when signed out; email, Sign out, Delete account when in. */
export function AccountMenu({ size = 40 }: { size?: number }) {
  const { user, loading, openSignIn, signOut, deleteAccount } = useAuth();
  const { data: saved } = useSavedArtists();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const wrap = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const confirmTitle = useId();

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => wrap.current && !wrap.current.contains(e.target as Node) && setOpen(false);
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (loading) return <span style={{ width: size, height: size }} aria-hidden="true" />;

  if (!user) {
    return (
      <button type="button" className="btn btn-ghost btn-sm" style={{ borderColor: 'transparent', color: 'var(--text-2)' }} onClick={() => openSignIn()}>
        Sign in
      </button>
    );
  }

  const label = user.name ?? user.email;
  const onDelete = async () => {
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteAccount();
      setConfirming(false);
      toast({ message: 'Your account and saved artists were deleted.' });
    } catch {
      setDeleteError('Couldn’t delete your account. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div ref={wrap} style={{ position: 'relative' }}>
      <button
        type="button"
        aria-label={`Account: ${label}`}
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen((o) => !o)}
        style={{
          width: size,
          height: size,
          borderRadius: 999,
          border: 0,
          padding: 0,
          background: '#2E4A7A',
          color: 'var(--text)',
          fontWeight: 600,
          fontSize: 14,
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        <Avatar name={label} src={user.avatarUrl} size={size} />
      </button>
      {open && (
        <div
          id={menuId}
          className="popover"
          style={{ position: 'absolute', right: 0, top: size + 8, zIndex: 30, width: 260, padding: 8, display: 'flex', flexDirection: 'column' }}
        >
          <div style={{ padding: '10px 12px 12px', borderBottom: '1px solid var(--line)' }}>
            {user.name && <div style={{ fontWeight: 600 }}>{user.name}</div>}
            <div className="muted ellipsis" style={{ fontSize: 14 }}>
              {user.email}
            </div>
          </div>
          <button
            type="button"
            className="text-btn"
            style={{ textAlign: 'left', color: 'var(--text)', fontSize: 15, padding: '0 12px' }}
            onClick={async () => {
              setOpen(false);
              await signOut();
            }}
          >
            Sign out
          </button>
          <button
            type="button"
            className="text-btn"
            style={{ textAlign: 'left', color: '#ffb4a8', fontSize: 15, padding: '0 12px' }}
            onClick={() => {
              setOpen(false);
              setConfirming(true);
            }}
          >
            Delete account
          </button>
        </div>
      )}
      <Modal open={confirming} onClose={() => !deleting && setConfirming(false)} labelledBy={confirmTitle}>
        <h2 id={confirmTitle} className="disp" style={{ margin: 0, fontSize: 26, fontWeight: 700, paddingRight: 40 }}>
          Delete your account?
        </h2>
        <p style={{ margin: '0 0 8px', color: 'var(--text-2)', lineHeight: 1.5 }}>
          This removes your account and {saved?.length ? `your ${saved.length} saved artists` : 'your saved artists'} right away. It
          can’t be undone. Your Spotify account isn’t affected.
        </p>
        {deleteError && (
          <p className="alert" role="alert">
            {deleteError}
          </p>
        )}
        <button type="button" className="btn" style={{ background: '#E5484D', color: '#fff' }} onClick={onDelete} disabled={deleting}>
          {deleting ? 'Deleting…' : 'Delete account'}
        </button>
        <button type="button" className="text-btn" onClick={() => setConfirming(false)} disabled={deleting}>
          Cancel
        </button>
      </Modal>
    </div>
  );
}
